import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

const DEFAULT_INTERVAL_MS = 60_000

export type AppVersionMeta = {
  version?: string
  commit?: string
  buildId?: string
  missing?: boolean
}

export function useAutoUpdater(initialVersionMeta: AppVersionMeta) {
  const intervalMs = Number(
    import.meta.env.VITE_AUTO_UPDATE_INTERVAL_MS ?? DEFAULT_INTERVAL_MS
  )

  const [pollingEnabled, setPollingEnabled] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setPollingEnabled(true), intervalMs)
    return () => clearTimeout(timer)
  }, [intervalMs])

  const { data: latestVersionMeta } = useQuery<AppVersionMeta>({
    queryKey: ['appVersionMeta'],
    queryFn: async () => {
      const res = await fetch(`/version.json?ts=${Date.now()}`, {
        cache: 'no-store'
      })
      if (res.status === 404) return { missing: true }
      if (!res.ok) throw new Error('Failed to fetch version.json')
      return (await res.json()) as AppVersionMeta
    },
    initialData: initialVersionMeta,
    enabled: pollingEnabled,
    staleTime: intervalMs,
    refetchInterval: pollingEnabled ? intervalMs : false,
    refetchOnWindowFocus: pollingEnabled,
    retry: 2
  })

  const updateAvailable = Boolean(
    initialVersionMeta.buildId &&
    latestVersionMeta?.buildId &&
    latestVersionMeta.buildId !== initialVersionMeta.buildId
  )

  return {
    initialVersionMeta,
    latestVersionMeta,
    updateAvailable
  }
}
